import { useLanguage } from '@/components/LanguageContext';
import LandingShellV3 from '@/components/landing/v3/LandingShellV3';
import CtaFinalOscuro from '@/components/landing/v3/CtaFinalOscuro';
import Estrellas from '@/components/landing/v3/Estrellas';
import FaqAcordeon from '@/components/landing/v3/FaqAcordeon';
import HeroProductoV3 from '@/components/landing/v3/HeroProductoV3';
import PorQueImporta from '@/components/landing/v3/PorQueImporta';
import SectionKicker from '@/components/landing/v3/SectionKicker';
import { pickLandingV3Content } from '@/pages/landingV3.content';

/**
 * AutomatizacionesIaV3 — respuestas automáticas y alertas.
 *
 * Portada desde `Automatizaciones.dc.html`.
 *
 * Las reglas que pinta la maqueta son las de verdad: el auto-responder acepta
 * de 1★ a 5★ (`MAX_REPLY_RATING_VALUE = 5`) y por defecto viene con 4★ y 5★
 * encendidas (`DEFAULT_REPLY_RATINGS`), que es justo lo que se enseña aquí.
 * Las alertas por nota, en cambio, solo van de 1★ a 3★ (`MAX_RATING_VALUE`).
 */
const NOTAS = [5, 4, 3, 2, 1];

export default function AutomatizacionesIaV3() {
  const { language } = useLanguage();
  const raiz = pickLandingV3Content(language);
  const c = raiz.producto.automatizaciones;
  const comun = raiz.producto.comun;

  return (
    <LandingShellV3>
      <HeroProductoV3
        titulo={c.hero.titulo}
        acento={c.hero.acento}
        tituloB={c.hero.tituloB}
        sub={c.hero.sub}
        cta={c.hero.cta}
        ctaVer={c.hero.ctaVer}
        anclaVer="#ejemplos"
        ctaTrialId="producto.automatizaciones.hero.trial"
        ctaVerId="producto.automatizaciones.hero.ver"
      />

      <Problema c={c.problema} etiquetaEstrellas={comun.estrellasEtiqueta} />
      <Reglas c={c.reglas} etiquetaEstrellas={comun.estrellasEtiqueta} />
      <Ejemplos c={c.ejemplos} etiquetaEstrellas={comun.estrellasEtiqueta} />
      <AlertaPalabra c={c.alertaPalabra} />
      <AlertaNota c={c.alertaNota} />

      <PorQueImporta
        kicker={c.porQue.kicker}
        cifra={c.porQue.cifra}
        cifraPie={c.porQue.cifraPie}
        titulo={c.porQue.titulo}
        parrafos={[c.porQue.p1, c.porQue.p2]}
        tonoCifra="red"
      />

      <FaqAcordeon titulo={c.faqTitulo} preguntas={c.faq} />

      <CtaFinalOscuro
        tituloA={c.cierre.titulo}
        acento={c.cierre.acento}
        subtitulo={c.cierre.sub}
        cta={comun.cierre.cta}
        microcopy={comun.cierre.microcopy}
        enterpriseA={comun.cierre.enterpriseA}
        enterpriseCta={comun.cierre.enterpriseCta}
        ctaTrial="producto.automatizaciones.cierre.trial"
        ctaVentas="producto.automatizaciones.cierre.ventas"
      />
    </LandingShellV3>
  );
}

const conNota = (plantilla, n) => plantilla.replace('{n}', n);

function Problema({ c, etiquetaEstrellas }) {
  const pendientes = [5, 4, 3, 5];

  return (
    <section className="px-6 pt-16">
      <div className="max-w-[1180px] mx-auto bg-v3-surface rounded-3xl p-7 sm:p-10 flex flex-wrap gap-8 lg:gap-11 items-center">
        <div className="flex-none w-28 flex flex-col gap-2.5">
          {pendientes.map((n, i) => (
            <div
              key={i}
              className="bg-white rounded-xl px-3 py-2.5 flex items-center gap-2 shadow-[0_1px_3px_rgba(0,0,0,.06)]"
            >
              <Estrellas
                n={n}
                etiqueta={conNota(etiquetaEstrellas, n)}
                className="text-[10.5px]"
              />
              <span className="flex-1" />
              <span className="w-[7px] h-[7px] rounded-full bg-v3-red flex-none" aria-hidden="true" />
            </div>
          ))}
          <div className="text-[11.5px] font-bold text-v3-red text-center mt-0.5">
            {c.pendientes}
          </div>
        </div>

        <div className="flex-1 min-w-[280px]">
          <h2 className="text-[26px] md:text-[31px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[26ch]">
            {c.titulo}
          </h2>
          <p className="text-base leading-[1.7] text-v3-body mt-4 max-w-[64ch]">{c.p1}</p>
          <p className="text-base leading-[1.7] text-v3-body mt-3.5 max-w-[64ch]">{c.p2}</p>
        </div>
      </div>
    </section>
  );
}

function Reglas({ c, etiquetaEstrellas }) {
  return (
    <section className="px-6 pt-[76px]">
      <div className="max-w-[1180px] mx-auto">
        <SectionKicker>{c.kicker}</SectionKicker>

        <div className="grid gap-6 lg:grid-cols-2 mt-7">
          <article className="bg-white border border-v3-line rounded-[20px] px-7 py-[30px]">
            <h2 className="text-[22px] md:text-2xl leading-[1.2] tracking-[-.02em] font-bold text-v3-ink max-w-[22ch]">
              {c.quien.titulo}
            </h2>
            <p className="text-[15.5px] leading-[1.65] text-v3-body mt-3">{c.quien.cuerpo}</p>

            <ul className="flex flex-col gap-2 mt-[22px]">
              {NOTAS.map((n) => {
                // De 4★ arriba, respuesta automática: es el `DEFAULT_REPLY_RATINGS`
                // real. Las de abajo quedan en «solo aviso», que también existe.
                const auto = n >= 4;
                return (
                  <li
                    key={n}
                    className="bg-v3-surface rounded-xl px-3.5 py-2.5 flex items-center gap-3"
                  >
                    <Estrellas
                      n={n}
                      etiqueta={conNota(etiquetaEstrellas, n)}
                      className="text-xs w-16 flex-none"
                    />
                    <span
                      className={`flex-1 text-[13.5px] font-semibold ${
                        auto ? 'text-v3-ink' : 'text-v3-faint'
                      }`}
                    >
                      {auto ? c.quien.estados.auto : c.quien.estados.aviso}
                    </span>
                    {/* Maqueta, no control: no se puede pulsar, así que va
                        oculto al árbol de accesibilidad y el estado ya lo
                        dice el texto de al lado. */}
                    <span
                      aria-hidden="true"
                      className={`w-[34px] h-5 rounded-full p-0.5 flex flex-none ${
                        auto ? 'bg-v3-gold justify-end' : 'bg-v3-track justify-start'
                      }`}
                    >
                      <span className="w-4 h-4 rounded-full bg-white block shadow-[0_1px_3px_rgba(0,0,0,.2)]" />
                    </span>
                  </li>
                );
              })}
            </ul>
          </article>

          <article className="bg-white border border-v3-line rounded-[20px] px-7 py-[30px]">
            <h2 className="text-[22px] md:text-2xl leading-[1.2] tracking-[-.02em] font-bold text-v3-ink max-w-[22ch]">
              {c.cuando.titulo}
            </h2>
            <p className="text-[15.5px] leading-[1.65] text-v3-body mt-3">{c.cuando.cuerpo}</p>

            <div className="bg-v3-surface rounded-2xl px-[18px] pt-5 pb-4 mt-[22px]">
              <div className="relative h-1.5 rounded-full bg-v3-track" aria-hidden="true">
                <div className="absolute left-[14%] w-[34%] inset-y-0 rounded-full bg-v3-gold" />
                <div className="absolute left-[14%] -top-1 w-3.5 h-3.5 rounded-full bg-white border-2 border-v3-gold -translate-x-1/2" />
                <div className="absolute left-[48%] -top-1 w-3.5 h-3.5 rounded-full bg-white border-2 border-v3-gold -translate-x-1/2" />
              </div>
              <div className="flex justify-between text-[11.5px] text-v3-faint mt-3">
                <span>{c.cuando.min}</span>
                <span>{c.cuando.max}</span>
              </div>
              <p className="flex items-center gap-2 flex-wrap mt-3.5 pt-3.5 border-t border-black/5 text-[13.5px] text-v3-body">
                {c.cuando.publicaEntre}
                <span className="font-bold bg-v3-goldsoft text-v3-golddeep rounded-full px-2.5 py-1">
                  {c.cuando.desde}
                </span>
                {c.cuando.y}
                <span className="font-bold bg-v3-goldsoft text-v3-golddeep rounded-full px-2.5 py-1">
                  {c.cuando.hasta}
                </span>
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

function Ejemplos({ c, etiquetaEstrellas }) {
  return (
    <section id="ejemplos" className="px-6 pt-6">
      <div className="max-w-[1180px] mx-auto bg-v3-surface rounded-3xl px-6 py-9 sm:px-8">
        <h2 className="text-[23px] md:text-[26px] leading-[1.2] tracking-[-.02em] font-bold text-v3-ink max-w-[24ch]">
          {c.titulo}
        </h2>
        <p className="text-base leading-[1.7] text-v3-body mt-3 max-w-[64ch]">{c.cuerpo}</p>

        <figure className="bg-white rounded-2xl px-[18px] py-4 mt-6 flex items-start gap-3 shadow-[0_1px_3px_rgba(0,0,0,.06)]">
          <span
            aria-hidden="true"
            className="w-8 h-8 rounded-full bg-[#ede9fe] text-[#6d28d9] flex items-center justify-center text-[13px] font-bold flex-none"
          >
            {c.resena.inicial}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[13.5px] font-bold tracking-[-.01em] text-v3-ink">
                {c.resena.autor}
              </span>
              <Estrellas
                n={c.resena.estrellas}
                etiqueta={conNota(etiquetaEstrellas, c.resena.estrellas)}
                className="text-[11px]"
              />
            </div>
            <blockquote className="text-sm leading-[1.55] text-v3-body mt-1.5">
              {c.resena.texto}
            </blockquote>
          </div>
        </figure>

        <div className="grid gap-4 sm:grid-cols-2 mt-4">
          <Respuesta etiqueta={c.aMano.etiqueta} texto={c.aMano.texto} />
          <Respuesta etiqueta={c.conIa.etiqueta} texto={c.conIa.texto} destacada />
        </div>

        <p className="text-base leading-[1.7] text-v3-ink font-semibold mt-5 max-w-[64ch]">
          {c.remate}
        </p>
      </div>
    </section>
  );
}

function Respuesta({ etiqueta, texto, destacada }) {
  return (
    <div
      className={`bg-white rounded-2xl ${
        destacada ? 'border-2 border-v3-gold p-[19px]' : 'border border-v3-line p-5'
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className={`w-2 h-2 rounded-full block ${destacada ? 'bg-v3-gold' : 'bg-v3-faint'}`}
        />
        <span
          className={`text-[12.5px] font-bold ${
            destacada ? 'text-v3-golddeep' : 'text-v3-muted'
          }`}
        >
          {etiqueta}
        </span>
      </div>
      <p
        className={`text-[15px] leading-[1.6] mt-3 ${
          destacada ? 'text-v3-ink' : 'text-v3-muted'
        }`}
      >
        {texto}
      </p>
    </div>
  );
}

function AlertaPalabra({ c }) {
  return (
    <section className="px-6 pt-[76px]">
      <div className="max-w-[1180px] mx-auto">
        <SectionKicker>{c.kicker}</SectionKicker>

        <div className="grid gap-10 lg:grid-cols-2 lg:gap-11 items-center mt-7">
          <div>
            <h2 className="text-[26px] md:text-[31px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[26ch]">
              {c.titulo}
            </h2>
            <p className="text-base leading-[1.7] text-v3-body mt-4 max-w-[60ch]">{c.p1}</p>
            <p className="text-base leading-[1.7] text-v3-body mt-3.5 max-w-[60ch]">{c.p2}</p>
          </div>

          <div className="flex flex-col gap-4">
            <GrupoChips
              etiqueta={c.urgente.etiqueta}
              chips={c.urgente.chips}
              fondo="bg-v3-redsoft"
              colorEtiqueta="text-v3-red"
              colorChip="text-[#b91c1c]"
            />
            <GrupoChips
              etiqueta={c.oportunidad.etiqueta}
              chips={c.oportunidad.chips}
              fondo="bg-v3-greensoft"
              colorEtiqueta="text-v3-green"
              colorChip="text-[#15803d]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function GrupoChips({ etiqueta, chips, fondo, colorEtiqueta, colorChip }) {
  return (
    <div className={`${fondo} rounded-[20px] px-6 py-[22px]`}>
      <div className={`text-[12.5px] font-bold ${colorEtiqueta}`}>{etiqueta}</div>
      <ul className="flex gap-2.5 flex-wrap mt-3.5">
        {chips.map((chip) => (
          <li
            key={chip}
            className={`bg-white ${colorChip} text-sm font-semibold rounded-full px-4 py-2.5`}
          >
            {chip}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AlertaNota({ c }) {
  const e = c.email;

  return (
    <section className="px-6 pt-[76px]">
      <div className="max-w-[1180px] mx-auto">
        <SectionKicker>{c.kicker}</SectionKicker>

        <div className="grid gap-10 lg:grid-cols-2 lg:gap-11 items-center mt-7">
          <div>
            <h2 className="text-[26px] md:text-[31px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[26ch]">
              {c.titulo}
            </h2>
            <p className="text-base leading-[1.7] text-v3-body mt-4 max-w-[60ch]">{c.cuerpo}</p>
          </div>

          <div className="bg-white border border-v3-line rounded-[20px] v3-shadow overflow-hidden max-w-[420px] mx-auto w-full">
            <div className="bg-v3-surface px-[18px] py-3.5 border-b border-black/5 flex items-center justify-between gap-2.5">
              <span className="text-xs font-semibold text-v3-muted">{e.bandeja}</span>
              <span className="text-[11.5px] text-v3-faint">{e.ahora}</span>
            </div>
            <div className="p-[18px]">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="w-[34px] h-[34px] rounded-full bg-v3-gold text-v3-ink flex items-center justify-center text-sm font-bold flex-none"
                >
                  M
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13.5px] font-bold tracking-[-.01em] text-v3-ink">
                    {e.de}
                  </span>
                  <span className="block text-[11.5px] text-v3-faint mt-px">{e.remite}</span>
                </span>
                <span
                  aria-hidden="true"
                  className="w-[9px] h-[9px] rounded-full bg-v3-red flex-none"
                />
              </div>
              <div className="text-[15.5px] font-bold tracking-[-.01em] mt-4 text-v3-ink">
                {e.asunto}
              </div>
              <p className="text-sm leading-[1.6] text-v3-muted mt-2">{e.extracto}</p>
              <div className="mt-4 pt-3.5 border-t border-black/5">
                <span className="text-xs font-semibold bg-v3-redsoft text-v3-red rounded-full px-2.5 py-1.5">
                  {e.sello}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
